import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useJobs } from "@/hooks/use-api";
import {
  addDays,
  addMonths,
  bucketByDate,
  dateKey,
  dayLabel,
  monthJobCount,
  monthLabel,
  monthsInSpan,
  nearestPopulatedMonth,
  spanIntersectsMonth,
} from "@/lib/calendar-grid";
import { CalendarMonthGrid } from "./calendar-month-grid";
import { CalendarMonthEmpty } from "./calendar-month-empty";
import { CalendarDayView } from "./calendar-day-view";
import { toCalendarJob, selectSpanningJobs } from "./calendar-job-mapper";
import type { CalendarJob } from "./job-chip";

type Row = Record<string, unknown>;
type Zoom = "month" | "day";

interface JobsCalendarProps {
  extraParams?: Record<string, string>;
}

function CalendarSkeleton() {
  return (
    <div className="grid grid-cols-7 gap-px rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-border)]">
      {Array.from({ length: 7 + 42 }).map((_, i) => (
        <div
          key={i}
          className={
            i < 7
              ? "bg-[color:var(--color-bg)] px-2 py-1.5"
              : "min-h-[104px] bg-[color:var(--color-surface)] p-1.5"
          }
        >
          {i >= 7 && i % 3 === 0 && (
            <Skeleton className="mt-5 h-4 w-full rounded-sm" />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Jobs calendar (ADV-786 slice 1) — Month and Day views. Self-contained like
 * the pipeline board: fetches the job set, buckets by `scheduled_date`, and
 * renders a fixed 6×7 Sunday-start month grid (decision C1) or a single-day
 * list (the rep's "what's my day"). Week view, the +N overflow popover, and
 * touch-swipe paging land in slice 2.
 */
export function JobsCalendar({ extraParams = {} }: JobsCalendarProps) {
  const navigate = useNavigate();
  const today = new Date();
  const todayKey = dateKey(today);

  const [zoom, setZoom] = useState<Zoom>("month");
  const [anchor, setAnchor] = useState<Date>(today);

  // The list endpoint caps `limit` at 100 (PaginationQuerySchema). 100 covers
  // the demo job set; slice 2 fetches by the visible date window instead.
  const { data, isLoading, isError } = useJobs({ limit: 100, ...extraParams });
  const jobs = useMemo(() => (data?.jobs ?? []) as Row[], [data?.jobs]);

  const byDate = useMemo(() => {
    const raw = bucketByDate(
      jobs,
      (j) => j.scheduled_date as string | undefined,
    );
    const mapped = new Map<string, CalendarJob[]>();
    for (const [key, rows] of raw) mapped.set(key, rows.map(toCalendarJob));
    return mapped;
  }, [jobs]);

  const spanningJobs = useMemo(() => selectSpanningJobs(jobs), [jobs]);

  const anchorYear = anchor.getFullYear();
  const anchorMonth = anchor.getMonth();

  const visibleCount = useMemo(
    () => monthJobCount(byDate, anchorYear, anchorMonth),
    [byDate, anchorYear, anchorMonth],
  );

  // Spanning jobs render as bars even with no `scheduled_date` bucket in the
  // month, so the month is only truly empty when neither day chips nor spans
  // intersect it (otherwise the cue lies over visible span bars).
  const spanningInMonth = useMemo(
    () =>
      spanningJobs.filter((j) =>
        spanIntersectsMonth(
          j.productionStartDate,
          j.productionEndDate,
          anchorYear,
          anchorMonth,
        ),
      ).length,
    [spanningJobs, anchorYear, anchorMonth],
  );

  // The fetch is capped at `limit: 100` and not scoped to the visible month, so
  // a truncated result set must not be treated as authoritative — announcing an
  // empty month from a partial page would be wrong for accounts with >100 jobs.
  const total = data?.total ?? jobs.length;
  const isResultComplete = jobs.length >= total;

  // Month keys (YYYY-MM) that spanning jobs make visible, so the jump target
  // considers span-only months (no scheduled_date bucket) too.
  const spanMonthKeys = useMemo(() => {
    const set = new Set<string>();
    for (const j of spanningJobs) {
      for (const key of monthsInSpan(
        j.productionStartDate,
        j.productionEndDate,
      ))
        set.add(key);
    }
    return set;
  }, [spanningJobs]);

  const nearestMonth = useMemo(() => {
    const nm = nearestPopulatedMonth(
      byDate,
      { year: anchorYear, month: anchorMonth },
      spanMonthKeys,
    );
    if (!nm) return null;
    if (nm.year === anchorYear && nm.month === anchorMonth) return null;
    // Count distinct jobs visible in the target month (scheduled chips ∪ span
    // bars) so a span-only month isn't reported as "0 jobs".
    const visibleIds = new Set<string>();
    const prefix = `${nm.year}-${String(nm.month + 1).padStart(2, "0")}`;
    for (const [key, bucket] of byDate) {
      if (key.slice(0, 7) === prefix)
        for (const j of bucket) visibleIds.add(j.id);
    }
    for (const j of spanningJobs) {
      if (
        spanIntersectsMonth(
          j.productionStartDate,
          j.productionEndDate,
          nm.year,
          nm.month,
        )
      )
        visibleIds.add(j.id);
    }
    return {
      year: nm.year,
      month: nm.month,
      count: visibleIds.size,
      label: monthLabel(nm.year, nm.month),
    };
  }, [byDate, anchorYear, anchorMonth, spanMonthKeys, spanningJobs]);

  const step = (delta: number) =>
    setAnchor((a) =>
      zoom === "day"
        ? addDays(a, delta)
        : (() => {
            const { year, month } = addMonths(
              a.getFullYear(),
              a.getMonth(),
              delta,
            );
            return new Date(year, month, 1);
          })(),
    );

  const rangeLabel =
    zoom === "day"
      ? dayLabel(anchor)
      : monthLabel(anchor.getFullYear(), anchor.getMonth());

  return (
    <div className="space-y-3">
      {/* Toolbar — zoom toggle + prev · today · next + absolute range label
          (§4.3). Lives in the calendar for slice 1; slice 2 lifts it into the
          aggregateStrip and adds Week. */}
      <div className="flex items-center gap-2">
        <ToggleGroup
          type="single"
          value={zoom}
          onValueChange={(v) => v && setZoom(v as Zoom)}
          variant="outline"
          size="sm"
          aria-label="Calendar zoom"
        >
          <ToggleGroupItem value="day">Day</ToggleGroupItem>
          <ToggleGroupItem value="month">Month</ToggleGroupItem>
        </ToggleGroup>

        <div className="ml-1 flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label={zoom === "day" ? "Previous day" : "Previous month"}
            onClick={() => step(-1)}
            className="size-8"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAnchor(today)}
            className="h-8"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label={zoom === "day" ? "Next day" : "Next month"}
            onClick={() => step(1)}
            className="size-8"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <h2 className="ml-1 typo-heading text-[color:var(--color-text-hi)]">
          {rangeLabel}
        </h2>
      </div>

      {isLoading && jobs.length === 0 ? (
        <CalendarSkeleton />
      ) : isError ? (
        // A failed fetch must not masquerade as an empty calendar (§1.5).
        <p className="py-16 text-center typo-body text-[color:var(--color-text-lo)]">
          Couldn't load the calendar. Refresh to try again.
        </p>
      ) : zoom === "day" ? (
        <CalendarDayView
          date={anchor}
          jobs={byDate.get(dateKey(anchor)) ?? []}
          isToday={dateKey(anchor) === todayKey}
          onOpenJob={(id) => navigate(`/jobs/${id}`)}
        />
      ) : (
        <>
          {!isLoading &&
            visibleCount === 0 &&
            spanningInMonth === 0 &&
            jobs.length > 0 &&
            isResultComplete && (
              <CalendarMonthEmpty
                monthLabel={monthLabel(anchorYear, anchorMonth)}
                nearest={nearestMonth}
                onJump={(target) =>
                  setAnchor(new Date(target.year, target.month, 1))
                }
              />
            )}
          <CalendarMonthGrid
            year={anchorYear}
            month={anchorMonth}
            byDate={byDate}
            spanningJobs={spanningJobs}
            todayKey={todayKey}
            onOpenJob={(id) => navigate(`/jobs/${id}`)}
          />
        </>
      )}
    </div>
  );
}
